import { uiPaper, uiSurface } from "shared/ui/uiSurface"
import { KeyboardArrowDown } from "@mui/icons-material"
import { Button, Menu, MenuItem, Stack } from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link as RouterLink, useLocation } from "react-router"
import { findActivePath, NavGroup, navGroups, navSlug } from "./navGroups"

/** Desktop app-bar navigation: one button per group, dropdown when the group has several pages. */
export const NavGroupLinks: FC<{ dark: boolean }> = ({ dark }) => {
  const location = useLocation()
  const activePath = findActivePath(location.pathname)

  return (
    <Stack
      {...uiSurface("UI-DLV2-006")}
      direction="row"
      spacing={0.5}
      alignItems="center"
    >
      {navGroups.map((group) => (
        <NavGroupButton
          key={group.key}
          group={group}
          activePath={activePath}
          dark={dark}
        />
      ))}
    </Stack>
  )
}

const NavGroupButton: FC<{
  group: NavGroup
  activePath: string | undefined
  dark: boolean
}> = ({ group, activePath, dark }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const Icon = group.icon
  const isActive = group.items.some((i) => i.path === activePath)
  const single = group.items.length === 1 ? group.items[0] : undefined

  const sx = {
    textTransform: "none",
    fontWeight: isActive ? 700 : 400,
    color: dark ? "#fff" : isActive ? "primary.main" : "text.secondary",
    bgcolor: isActive
      ? dark
        ? alpha("#fff", 0.12)
        : alpha(theme.palette.primary.main, 0.08)
      : "transparent",
    borderRadius: 2,
    px: 1,
    minWidth: "auto",
    whiteSpace: "nowrap",
    // 8 groups fit a 1280px bar only without icons; show them from xl up
    "& .MuiButton-startIcon": { display: { xs: "none", xl: "inherit" } },
  } as const

  if (single) {
    return (
      <Button
        component={RouterLink}
        to={single.path}
        data-testid={`nav-${navSlug(single.path)}-link`}
        size="small"
        startIcon={<Icon fontSize="small" />}
        sx={sx}
      >
        {t(group.labelKey)}
      </Button>
    )
  }

  return (
    <>
      <Button
        data-testid={`nav-group-${group.key}-btn`}
        size="small"
        startIcon={<Icon fontSize="small" />}
        endIcon={<KeyboardArrowDown fontSize="small" />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        sx={sx}
      >
        {t(group.labelKey)}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: { ...uiPaper("UI-DLV2-012"), sx: { mt: 0.5, minWidth: 200 } },
        }}
      >
        {group.items.map((item) => (
          <MenuItem
            key={item.path}
            component={RouterLink}
            to={item.path}
            data-testid={`nav-${navSlug(item.path)}-link`}
            selected={item.path === activePath}
            onClick={() => setAnchorEl(null)}
          >
            {t(item.labelKey)}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
